<script>
import { GlButton, GlEmptyState, GlLoadingIcon } from '@gitlab/ui';
import emptyStateSvg from '@gitlab/svgs/dist/illustrations/status/status-new-sm.svg';
import NonGfmMarkdown from '~/vue_shared/components/markdown/non_gfm_markdown.vue';
import DuoChatQuickAction from 'ee/ai/shared/widgets/duo_chat_quick_action.vue';
import { buildWorkPlanChatCommand } from './constants';

export default {
  name: 'WorkPlanView',
  components: {
    GlButton,
    GlEmptyState,
    GlLoadingIcon,
    NonGfmMarkdown,
    DuoChatQuickAction,
  },
  emptyStateSvg,
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    canUpdate: {
      type: Boolean,
      required: true,
    },
    savedContent: {
      type: String,
      required: true,
    },
    workItemId: {
      type: String,
      required: true,
    },
    workItemWebUrl: {
      type: String,
      required: true,
    },
  },
  emits: ['start-edit'],
  computed: {
    hasSavedContent() {
      return Boolean(this.savedContent);
    },
    generateWorkplanQuickAction() {
      return {
        command: buildWorkPlanChatCommand(this.workItemWebUrl),
        tracking: { label: 'create_work_plan' },
        buttonOptions: { variant: 'confirm', category: 'primary' },
      };
    },
  },
};
</script>

<template>
  <div class="work-item-detail-panel-content gl-px-5 gl-pt-4">
    <gl-loading-icon v-if="isLoading" size="md" class="gl-my-5" />
    <template v-else-if="!hasSavedContent">
      <gl-empty-state
        v-if="canUpdate"
        data-testid="work-plan-empty-state"
        :svg-path="$options.emptyStateSvg"
        :svg-height="72"
        content-class="!gl-px-0"
        :title="s__('AgentPlan|Create a workplan')"
        :description="
          s__(
            'AgentPlan|A workplan breaks down your work item into a step-by-step implementation guide for GitLab Duo to execute. Create one manually or let Duo generate it for you.',
          )
        "
      >
        <template #actions>
          <div class="gl-flex gl-flex-wrap gl-justify-center gl-gap-3">
            <duo-chat-quick-action
              :resource-id="workItemId"
              :button-text="s__('AgentPlan|Generate')"
              :command="generateWorkplanQuickAction.command"
              :tracking-info="generateWorkplanQuickAction.tracking"
              :button-options="generateWorkplanQuickAction.buttonOptions"
              data-testid="panel-generate-with-duo-button"
            />
            <gl-button
              category="secondary"
              data-testid="panel-create-manually-button"
              @click="$emit('start-edit')"
            >
              {{ s__('AgentPlan|Create manually') }}
            </gl-button>
          </div>
        </template>
      </gl-empty-state>
      <p v-else data-testid="work-plan-empty-state" class="gl-mb-0 gl-text-subtle">
        {{
          s__(
            "AgentPlan|No workplan has been added yet. You don't have permission to create or edit the workplan.",
          )
        }}
      </p>
    </template>
    <template v-else>
      <non-gfm-markdown :markdown="savedContent" data-testid="work-plan-rendered" />
    </template>
  </div>
</template>
